/* Public events: list (upcoming + past) and detail (with manual donation + pledge form). */
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { Link, navigate } from "../../lib/router-compat.jsx";
import PublicShell from "../../components/shell/PublicShell.jsx";
import { HeroBlock } from "../../components/SectionRenderer.jsx";
import DonationDetails from "../../components/DonationDetails.jsx";
import RichText from "../../ui/RichText.jsx";
import { Card, SmartImage, Skeleton, EmptyState, Badge, Button, Field, Input, Textarea, Select, Icon, showToast } from "../../ui/index.js";
import { useEvents, useEvent, useSiteSettings } from "../../data/hooks.js";
import { addLead } from "../../data/mutations.js";
import { formatDate, CURRENCY_FMT, toDate } from "../../utils/format.js";

/* An event rendered as a full-width feature section (image + text, alternating),
   mirroring the programs page. The image is constrained (object-fit: cover) so
   large uploads never overflow. */
function EventRow({ ev, warm, flip }) {
  const to = `/events/${ev.slug}`;
  return (
    <section className={`section ${warm ? "section--warm" : ""}`}>
      <div className="container">
        <div className={`feature-row ${flip ? "feature-row--flip" : ""}`}>
          <Link to={to} className="feature-row__media" aria-label={ev.title}>
            <SmartImage src={ev.heroImage?.url} alt={ev.heroImage?.alt || ev.title} label={ev.title} />
          </Link>
          <div className="feature-row__content">
            <div className="row" style={{ gap: 8, alignItems: "center" }}>
              <Badge variant={ev.status === "upcoming" ? "success" : "neutral"} noDot>{ev.status === "upcoming" ? "Upcoming" : "Past"}</Badge>
              <span className="t-sm muted"><Icon.Calendar size={14} style={{ verticalAlign: "-2px" }} /> {formatDate(ev.startAt)}</span>
            </div>
            <h2 className="t-3xl" style={{ margin: 0 }}>{ev.title}</h2>
            {ev.location && <div className="t-base muted"><Icon.MapPin size={16} style={{ verticalAlign: "-3px" }} /> {ev.location}</div>}
            {ev.summary && <p className="t-lg muted" style={{ margin: 0 }}>{ev.summary}</p>}
            <Link to={to} className="btn btn--secondary"><Icon.ArrowRight size={16} /> View event</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function EventsPage() {
  const { events, loading } = useEvents({ publishedOnly: true });
  const upcoming = events.filter((e) => e.status === "upcoming");
  const past = events.filter((e) => e.status !== "upcoming");

  return (
    <PublicShell>
      <Helmet><title>Events — Purple Angels Kids Foundation</title></Helmet>
      <section className="page-header">
        <div className="container">
          <h1 className="t-4xl" style={{ margin: 0 }}>Events</h1>
          <p className="page-header__subtitle">Join us, or support an upcoming event.</p>
        </div>
      </section>

      {loading ? (
        <section className="section"><div className="container"><Skeleton h={320} /></div></section>
      ) : events.length === 0 ? (
        <section className="section">
          <div className="container"><EmptyState icon={<Icon.Calendar size={40} />} title="No events yet" body="Upcoming events will appear here." /></div>
        </section>
      ) : (
        <>
          {upcoming.length > 0 && (
            <>
              <div className="container" style={{ paddingTop: 48 }}><h2 className="t-2xl" style={{ margin: 0 }}>Upcoming</h2></div>
              {upcoming.map((e, i) => <EventRow key={e.id} ev={e} warm={i % 2 === 1} flip={i % 2 === 1} />)}
            </>
          )}
          {past.length > 0 && (
            <>
              <div className="container" style={{ paddingTop: 48 }}><h2 className="t-2xl" style={{ margin: 0 }}>Past events</h2></div>
              {past.map((e, i) => <EventRow key={e.id} ev={e} warm={i % 2 === 1} flip={i % 2 === 1} />)}
            </>
          )}
        </>
      )}
    </PublicShell>
  );
}

export function EventDetailPage() {
  const { slug } = useParams();
  const { event, loading } = useEvent(slug);
  const { settings } = useSiteSettings();

  if (loading) {
    return (
      <PublicShell>
        <div className="container" style={{ padding: "64px 0" }}><Skeleton h={360} /></div>
      </PublicShell>
    );
  }
  if (!event || event.published === false) {
    return (
      <PublicShell>
        <div className="container" style={{ padding: "64px 0" }}>
          <EmptyState title="Event not found" body="This event may have been removed." action={<Link to="/events" className="btn btn--secondary">All events</Link>} />
        </div>
      </PublicShell>
    );
  }

  const donationEnabled = event.donation?.enabled;
  const donation = donationEnabled ? event.donation : settings?.donation;
  const goal = event.donation?.goalAmount;
  const raised = event.totals?.receivedAmount || 0;
  const currency = event.donation?.currency || "GHS";
  const pct = goal ? Math.min(100, Math.round((raised / goal) * 100)) : 0;

  return (
    <PublicShell>
      <Helmet><title>{event.title} — Purple Angels Kids Foundation</title></Helmet>
      <HeroBlock section={{ eyebrow: event.status === "upcoming" ? "Upcoming event" : "Event", headline: event.title, subheadline: event.summary, image: event.heroImage }} />

      <section className="section">
        <div className="container">
          <div className="row" style={{ gap: 24, flexWrap: "wrap", marginBottom: 24 }}>
            <span className="t-base"><Icon.Calendar size={18} style={{ verticalAlign: "-3px", marginRight: 6 }} />{formatDate(event.startAt, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</span>
            {event.location && <span className="t-base"><Icon.MapPin size={18} style={{ verticalAlign: "-3px", marginRight: 6 }} />{event.location}</span>}
          </div>
          {event.bodyHtml && <div className="prose" style={{ maxWidth: 760 }}><RichText html={event.bodyHtml} className="" /></div>}
        </div>
      </section>

      {donationEnabled && (
        <section className="section section--warm">
          <div className="container">
            <div className="split-3-1 donate-page-row" style={{ gridTemplateColumns: "1fr 1fr", gap: 64 }}>
              <div>
                <h2 className="t-3xl" style={{ marginTop: 0 }}>Support this event</h2>
                {goal > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div className="row row--between" style={{ marginBottom: 8 }}>
                      <span className="t-base" style={{ fontWeight: 600 }}>{CURRENCY_FMT(raised, currency)} raised</span>
                      <span className="t-sm muted">Goal {CURRENCY_FMT(goal, currency)}</span>
                    </div>
                    <div className="alloc-row__bar"><span style={{ width: pct + "%" }} /></div>
                  </div>
                )}
                <p className="t-base muted">Transfer using the details below, then submit the form so we can credit your gift to this event.</p>
                <div style={{ marginTop: 24 }}><DonationDetails donation={donation} /></div>
              </div>
              <div style={{ alignSelf: "flex-start", position: "sticky", top: 96 }}>
                <EventPledgeForm event={event} />
              </div>
            </div>
          </div>
        </section>
      )}
    </PublicShell>
  );
}

function EventPledgeForm({ event }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", amount: "", currency: event.donation?.currency || "GHS", method: "either", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const v = {};
    if (!form.name.trim()) v.name = "Full name is required.";
    if (!form.email.trim()) v.email = "Email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) v.email = "Please enter a valid email.";
    if (!form.amount || Number(form.amount) <= 0) v.amount = "Enter an amount.";
    setErrors(v);
    if (Object.keys(v).length) return;
    setSubmitting(true);
    try {
      await addLead({
        donorName: form.name.trim(), donorEmail: form.email.trim(), donorPhone: form.phone.trim(),
        country: "Ghana", organization: "", intendedAmount: Number(form.amount), currency: form.currency,
        method: form.method, message: form.message.trim(), eventId: event.slug, source: "event",
      });
      navigate(`/donate/thank-you?email=${encodeURIComponent(form.email)}`);
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't submit", body: err.message });
      setSubmitting(false);
    }
  };

  return (
    <Card variant="elevated" style={{ padding: 32, boxShadow: "var(--shadow-lg)" }}>
      <h3 className="t-2xl" style={{ marginTop: 0 }}>Pledge to this event</h3>
      <p className="t-sm muted" style={{ marginTop: 4, marginBottom: 24 }}>Submit your details and we'll confirm your gift.</p>
      <form onSubmit={submit} noValidate className="stack-lg">
        <Field label="Full name" required error={errors.name}><Input value={form.name} onChange={update("name")} error={!!errors.name} /></Field>
        <Field label="Email" required error={errors.email}><Input type="email" value={form.email} onChange={update("email")} error={!!errors.email} /></Field>
        <Field label="Phone (optional)"><Input type="tel" value={form.phone} onChange={update("phone")} placeholder="+233…" /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }} className="form-grid-2">
          <Field label="Amount" required error={errors.amount}><Input type="number" min="0" value={form.amount} onChange={update("amount")} error={!!errors.amount} /></Field>
          <Field label="Currency"><Select value={form.currency} onChange={update("currency")}><option>GHS</option><option>USD</option><option>GBP</option><option>EUR</option></Select></Field>
        </div>
        <Field label="Anything else? (optional)"><Textarea value={form.message} onChange={update("message")} rows={3} /></Field>
        <Button type="submit" variant="primary" size="lg" loading={submitting} className="btn--full">{submitting ? "Submitting..." : "Submit pledge"}</Button>
      </form>
    </Card>
  );
}

export default EventsPage;
